// EditProject.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ClipboardList } from "lucide-react";
import "./Project.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    contact_person: "",
    contact_phone: "",
    location: "",
    image: null
  });

  const [preview, setPreview] = useState("");

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${url}/api/projects/${id}`);
        const project = response.data.data;

        setData({
          title: project.title,
          description: project.description,
          start_date: project.start_date,
          end_date: project.end_date,
          contact_person: project.contact_person,
          contact_phone: project.contact_phone,
          location: project.location,
          image: null
        });

        if (project.image) {
          setPreview(`${url}/uploads/${project.image}`);
        }
      } catch (err) {
        toast.error("Failed to load project");
        navigate("/ViewProjects");
      }
    };

    fetchProject();
  }, [id, navigate]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files.length > 0) {
      setData({ ...data, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setData({ ...data, [name]: value });
    }
  };

  // Submit update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== null) formData.append(key, data[key]);
      });

      const response = await axios.put(`${url}/api/projects/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        toast.success("Project updated successfully!");
        navigate("/ViewProjects");
      }
    } catch (err) {
      toast.error("Failed to update project");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-wrapper">
      <div className="project-form-container">
        <h2 className="form-title">
          <ClipboardList size={20} className="inline mr-2" /> Edit Project
        </h2>

        <form className="project-form" onSubmit={handleSubmit}>
          {/* Shared fields */}
          {[
            "title",
            "description",
            "start_date",
            "end_date",
            "contact_person",
            "contact_phone",
            "location"
          ].map((field) => (
            <div key={field} className="form-field filled">
              <input
                type={field.includes("date") ? "date" : "text"}
                name={field}
                value={data[field] || ""}
                onChange={handleChange}
              />
              <label>
                {field.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </label>
            </div>
          ))}

          {/* File input */}
          <div className="form-field">
            <label>Project Image</label>
            <input type="file" name="image" onChange={handleChange} />
            {preview && <img src={preview} alt="Preview" width="80" />}
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate("/ViewProjects")}
            >
              Cancel
            </button>

            <button className="submit-btn" type="submit" disabled={submitting}>
              {submitting ? "Updating..." : "Update Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProject;
